import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useConnectWhatsAppCoexistence } from '@/api/mutations/omnichannel';
import { loadFacebookSDK } from '@/lib/utils/facebook-sdk';
import {
  launchWhatsAppCoexistenceSignup,
  parseWhatsAppCoexistenceWabaId,
  resolveWhatsAppCoexistenceConfig,
} from '@/lib/omnichannel/whatsapp-coexistence';

interface UseWhatsAppCoexistenceConnectOptions {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const useWhatsAppCoexistenceConnect = (
  options: UseWhatsAppCoexistenceConnectOptions = {}
) => {
  const { onSuccess, onError } = options;
  const [isLaunching, setIsLaunching] = useState(false);
  const pendingCodeRef = useRef<string | null>(null);
  const pendingWabaIdRef = useRef<string | null>(null);
  const submittedRef = useRef(false);
  const listenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const { mutate: connectCoexistence, isPending } =
    useConnectWhatsAppCoexistence();

  const cleanupListener = useCallback(() => {
    if (listenerRef.current) {
      window.removeEventListener('message', listenerRef.current);
      listenerRef.current = null;
    }
  }, []);

  const resetPending = useCallback(() => {
    pendingCodeRef.current = null;
    pendingWabaIdRef.current = null;
    submittedRef.current = false;
  }, []);

  const submitWhenReady = useCallback(() => {
    const code = pendingCodeRef.current;
    const wabaId = pendingWabaIdRef.current;
    if (!code || !wabaId || submittedRef.current) return;

    submittedRef.current = true;
    cleanupListener();

    connectCoexistence(
      { code, wabaId },
      {
        onSuccess: () => {
          setIsLaunching(false);
          resetPending();
          onSuccess?.();
        },
        onError: (error) => {
          setIsLaunching(false);
          resetPending();
          const message =
            error instanceof Error
              ? error.message
              : 'WhatsApp connect failed. Please try again.';
          onError?.(message);
        },
      }
    );
  }, [cleanupListener, connectCoexistence, onError, onSuccess, resetPending]);

  const start = useCallback(async () => {
    if (isLaunching || isPending) return;

    resetPending();
    cleanupListener();
    setIsLaunching(true);

    try {
      const { appId, configId } = resolveWhatsAppCoexistenceConfig();
      await loadFacebookSDK(appId, 'v21.0');

      const onMessage = (event: MessageEvent) => {
        const wabaId = parseWhatsAppCoexistenceWabaId(event);
        if (!wabaId) return;
        pendingWabaIdRef.current = wabaId;
        submitWhenReady();
      };

      listenerRef.current = onMessage;
      window.addEventListener('message', onMessage);

      launchWhatsAppCoexistenceSignup(
        configId,
        (code) => {
          pendingCodeRef.current = code;
          submitWhenReady();
        },
        () => {
          cleanupListener();
          resetPending();
          setIsLaunching(false);
        }
      );
    } catch (error) {
      cleanupListener();
      resetPending();
      setIsLaunching(false);
      const message =
        error instanceof Error
          ? error.message
          : 'Could not start WhatsApp Business App connect.';
      toast.error(message);
      onError?.(message);
    }
  }, [
    cleanupListener,
    isLaunching,
    isPending,
    onError,
    resetPending,
    submitWhenReady,
  ]);

  return {
    start,
    isPending: isLaunching || isPending,
  };
};
